import { useSyncExternalStore } from "react";
import { addDays, isToday, startOfDay } from "date-fns";

// The side panel lives outside the dashboard's tree but lists the jobs of the day it shows.
let day = new Date();
// Picked as "today", it stays today: a tab left open overnight moves on to the new day.
let followsToday = true;
const listeners = new Set<() => void>();
let midnightTimer: ReturnType<typeof setTimeout> | undefined;

const notify = () => listeners.forEach((listener) => listener());

function rollOver() {
  if (followsToday && !isToday(day)) {
    day = new Date();
    notify();
  }
}

function scheduleMidnight() {
  clearTimeout(midnightTimer);
  if (!listeners.size) return;
  const untilMidnight = startOfDay(addDays(new Date(), 1)).getTime() - Date.now();
  midnightTimer = setTimeout(() => {
    rollOver();
    scheduleMidnight();
  }, untilMidnight + 1000);
}

export const getDashboardDay = () => day;

export function setDashboardDay(next: Date) {
  followsToday = isToday(next);
  if (next.getTime() === day.getTime()) return;
  day = next;
  notify();
}

// A woken laptop fires the midnight timer late; coming back to the tab catches up first.
const onReturn = () => {
  if (document.visibilityState === "hidden") return;
  rollOver();
  scheduleMidnight();
};

export const subscribeDashboardDay = (listener: () => void) => {
  listeners.add(listener);
  if (listeners.size === 1) {
    rollOver();
    scheduleMidnight();
    if (typeof window !== "undefined") {
      window.addEventListener("focus", onReturn);
      document.addEventListener("visibilitychange", onReturn);
    }
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size) return;
    clearTimeout(midnightTimer);
    if (typeof window !== "undefined") {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    }
  };
};

export const useDashboardDay = () => useSyncExternalStore(subscribeDashboardDay, getDashboardDay);
